/**
 * Sign in.
 *
 * A single screen that moves through up to three steps:
 *
 *   phone → (hi →) otp → done
 *
 * PIN sign-in was removed (client decision, Oct 2026). On a shared office
 * browser it showed the next person the previous user's masked number and a PIN
 * they never set, with no way forward. Every login is now a WhatsApp OTP; the
 * trade is one OTP per login against a dead-end screen on a shared device. On
 * logout the remembered number and all cached data are wiped (see lib/auth), so
 * the next person starts clean.
 *
 * THE 'hi' STEP
 *
 * Meta will not approve an authentication template for this number until the
 * business has 2,000 unique users (client decision 12). Without a template,
 * WhatsApp delivers a message only inside the 24-hour window that opens when
 * the user messages the business. So a first-time login needs the user to send
 * one message before a code can reach them.
 *
 * The server refuses the send with WHATSAPP_WINDOW_CLOSED and hands back a
 * wa.me link. This screen turns that into one tappable button. Two details
 * carry most of the usability:
 *
 *   * The reason given is WhatsApp's rule, not our failure. A retailer told
 *     "sending failed" concludes the app is broken and phones the order in.
 *   * When they come back to the app after sending, the code is requested
 *     automatically. Asking someone to leave, act, return, and then remember
 *     to press one more button loses people at the last step.
 */

import { useEffect, useRef, useState } from 'react';
import { phoneInputDigits } from '../../lib/phone';
import { useNavigate } from 'react-router-dom';
import { authErrorMessage, homeRouteFor, recallPhone, rememberPhone, useAuth } from '../../lib/auth';
import { ApiError } from '../../lib/api';
import { requestOnceAfterLogin } from '../../lib/push';
import { useI18n, useT } from '../../lib/i18n';
import { Banner, Button, TextInput } from '../../components/ui';
import { LanguageToggle } from '../../components/Shell';
import { maskPhone } from '../../lib/format';
import { IconWhatsApp } from '../../components/Icons';

// PIN sign-in was removed (client decision, Oct 2026): a shared office browser
// kept showing the next person the previous user's masked number and a PIN they
// never set, with no way forward. Every login now goes phone → "hi" → OTP.
type Step = 'phone' | 'hi' | 'otp';

/** What the server tells us when the WhatsApp window is shut. */
interface HiPrompt {
  businessNumber: string;
  waLink: string;
  neverMessaged: boolean;
  /**
   * True when Meta itself refused the send, rather than our own window record
   * saying it looked shut. It decides whether "try anyway" is still on offer:
   * after Meta has said no there is nothing left to override.
   */
  attempted: boolean;
}

export default function Login() {
  const t = useT();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const { requestOtp, verifyOtp, devLogin, user } = useAuth();

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [hi, setHi] = useState<HiPrompt | null>(null);
  const [waOpened, setWaOpened] = useState(false);

  // Prefill the last number used. On logout the remembered number is wiped (see
  // logout() in lib/auth), so a shared browser starts blank for the next person.
  useEffect(() => {
    void (async () => {
      const last = await recallPhone();
      if (last) setPhone(last.replace('+91', ''));
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    navigate(homeRouteFor(user.role), { replace: true });

    // Ask for notification permission here rather than on first load. A prompt
    // shown before someone knows what the app is gets denied, and a denial
    // cannot be re-requested — they would have to dig through browser
    // settings. Fire-and-forget: declining must not block the login.
    void requestOnceAfterLogin();
  }, [user, navigate]);

  // Resend cooldown. Without it, an impatient user burns through the hourly
  // OTP rate limit in fifteen seconds and locks themselves out.
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [resendIn]);

  /**
   * When they come back from WhatsApp, ask for the code automatically.
   *
   * The sequence is: tap the button, the app loses focus, they send "hi",
   * they switch back. At that moment the window is open and they should not
   * have to press anything else — a step that requires the user to remember
   * one more tap after leaving and returning is a step people abandon.
   *
   * Guarded by `waOpened` so this only fires for someone who actually left,
   * and `silent` so a webhook that has not landed yet produces no error.
   */
  useEffect(() => {
    if (step !== 'hi' || !waOpened) return;

    function onVisible() {
      if (document.visibilityState !== 'visible') return;
      setWaOpened(false);
      // force=true: they have just come back from WhatsApp, so they have
      // almost certainly sent the message. Waiting for our webhook record to
      // catch up would make the common case fail — and would fail completely
      // wherever the webhook is not delivering at all.
      void onSendOtp(true, true);
    }

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // onSendOtp is stable enough for this purpose; re-subscribing on every
    // render would tear the listener down mid-handoff.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, waOpened]);

  const normalised = () => {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10 ? `+91${digits}` : digits.startsWith('91') ? `+${digits}` : phone;
  };

  const validPhone = /^[6-9]\d{9}$/.test(phone.replace(/\D/g, '').slice(-10));

  /**
   * Ask for a code.
   *
   * `silent` is for the automatic retry when the user returns from WhatsApp:
   * if the window still is not open, stay put and say nothing rather than
   * flashing an error at someone who has done nothing wrong — WhatsApp
   * webhooks can take a second or two to arrive.
   */
  async function onSendOtp(silent = false, force = false) {
    if (!silent) { setError(''); }
    setBusy(true);
    try {
      await requestOtp(normalised(), force);
      await rememberPhone(normalised());
      setResendIn(45);
      setNotice(t('auth.codeSentTo', { phone: maskPhone(normalised()) }));
      setHi(null);
      setError('');
      setStep('otp');
      setOtp('');
    } catch (err) {
      // The window is shut. Not an error the user caused, and not one they can
      // fix by retrying — so it gets its own screen rather than a red banner.
      if (err instanceof ApiError && err.code === 'WHATSAPP_WINDOW_CLOSED') {
        const f = err.fields ?? {};
        setHi({
          businessNumber: f.businessNumber ?? '',
          waLink: f.waLink ?? '',
          neverMessaged: f.neverMessaged === 'true',
          attempted: f.attempted === 'true',
        });
        setStep('hi');
        if (!silent) setError('');
        return;
      }
      if (!silent) setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function onContinueFromPhone() {
    setError('');
    if (!validPhone) { setError(t('auth.mobile')); return; }
    await rememberPhone(normalised());
    // Straight to the code — there is no PIN path any more.
    await onSendOtp();
  }

  async function onVerifyOtp() {
    setError('');
    if (otp.length !== 6) { setError(t('auth.enterCode')); return; }
    setBusy(true);
    try {
      const result = await verifyOtp(normalised(), otp);
      if (result.needsRegistration) {
        setError('');
        setNotice('');
        navigate('/register', {
          replace: true,
          state: { phone: normalised(), ticket: result.registrationTicket },
        });
        return;
      }
      // On success the effect above navigates to the role's home screen.
    } catch (err) {
      setError(authErrorMessage(err));
      setOtp('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app">
      <main className="screen" style={{ paddingBottom: 24, maxWidth: 420 }}>
        <div className="stack-4" style={{ paddingTop: 32 }}>
          <div className="stack-2">
            <img
              src="/icon-192.png"
              width={56}
              height={56}
              alt={t('app.name')}
              style={{
                width: 56, height: 56, borderRadius: 14, objectFit: 'contain',
                background: '#ffffff', boxShadow: '0 1px 4px rgba(15, 23, 42, 0.12)',
              }}
            />
            <h1 className="t-2xl">{t('app.name')}</h1>
            <p className="t-sm t-muted">
              {step === 'phone' && t('auth.welcomeLine')}
              {step === 'hi' && t('auth.hiTitle')}
              {step === 'otp' && t('auth.enterCode')}
            </p>
          </div>

          {error && <Banner tone="danger">{error}</Banner>}
          {!error && notice && step === 'otp' && <Banner tone="info">{notice}</Banner>}

          {step === 'phone' && (
            <form
              className="stack-4"
              onSubmit={(e) => { e.preventDefault(); void onContinueFromPhone(); }}
            >
              {/* Bold red, because the OTP only arrives by WhatsApp and will not
                  come at all unless the user has messaged this number first and
                  kept it saved. */}
              <p
                style={{
                  color: '#c62828', fontWeight: 700, fontSize: 13, lineHeight: 1.45, margin: 0,
                }}
              >
                {t('auth.otpNote', { number: '+91 88244 89593' })}
              </p>
              <TextInput
                label={t('auth.mobile')}
                hint={t('auth.mobileHint')}
                required
                // Deliberately NOT `numeric`: that right-aligns the field so
                // decimal points line up, which is right for money and wrong
                // for a phone number. `inputMode` still gives the number pad.
                autoComplete="tel"
                inputMode="numeric"
                placeholder="98765 43210"
                value={phone}
                onChange={(e) => setPhone(phoneInputDigits(e.target.value))}
              />
              <Button type="submit" block size="lg" busy={busy} disabled={!validPhone}>
                {t('app.next')}
              </Button>
            </form>
          )}

          {/* DEV / TEST ONLY. Stripped from the production build (import.meta.env.DEV
              is false there), and the server route it calls 404s unless ENVIRONMENT
              is dev/test — so this cannot appear or work against the live system. */}
          {step === 'phone' && import.meta.env.DEV && (
            <div
              className="stack-2"
              style={{ marginTop: 20, padding: 12, border: '1px dashed var(--line)', borderRadius: 'var(--radius)' }}
            >
              <div className="t-xs t-muted" style={{ fontWeight: 700 }}>Test login (dev only) — no OTP</div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                {([
                  ['owner_admin', 'Admin'],
                  ['salesperson', 'Salesman'],
                  ['driver', 'Driver'],
                  ['field_agent', 'Field agent'],
                  ['other_staff', 'Other staff'],
                ] as const).map(([role, label]) => (
                  <Button
                    key={role}
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      setError('');
                      try { await devLogin(role); }
                      catch { setError('Test login failed — is the local API running with ENVIRONMENT=dev and the seed loaded?'); }
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {step === 'hi' && hi && (
            <div className="stack-4">
              <Banner tone={hi.attempted ? 'warn' : 'info'}>
                {hi.attempted
                  ? t('auth.hiRefused')
                  : hi.neverMessaged ? t('auth.hiWhy') : t('auth.hiWhyAgain')}
              </Banner>

              {/*
                A real link, not a button with window.open().
                iOS Safari blocks window.open outside a direct user gesture and
                treats an async one as a popup — the tap would do nothing at
                all, on the single most important control on this screen.
                target=_blank keeps the login state alive underneath.
              */}
              <a
                className="btn btn--lg btn--block"
                href={hi.waLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setWaOpened(true)}
                style={{ gap: 8 }}
              >
                <IconWhatsApp width={20} height={20} />
                {t('auth.hiOpen')}
              </a>

              <p className="t-xs t-muted" style={{ textAlign: 'center' }}>
                {t('auth.hiSendTo', { number: hi.businessNumber })}
              </p>

              {/*
                The explicit path the client asked for: someone who has already
                messaged the business must not be walked through the instruction
                again.

                It passes force=true, which skips our own window record and asks
                Meta directly. That record comes from a webhook, and a webhook
                can be silent for reasons that are entirely ours — an
                unpublished Meta app receives no production callbacks at all. In
                that state the user HAS messaged the business and our table
                still says no, so trusting our own record here would lock
                everyone out over a configuration fault.
              */}
              <Button block busy={busy} onClick={() => void onSendOtp(false, true)}>
                {t('auth.hiAlreadySent')}
              </Button>

              <p className="t-xs t-faint">
                {t('auth.hiManual', { number: hi.businessNumber })}
              </p>

              <Button
                variant="ghost" block disabled={busy}
                onClick={() => { setHi(null); setError(''); setStep('phone'); }}
              >
                {t('auth.hiChanged')}
              </Button>
            </div>
          )}

          {step === 'otp' && (
            <form className="stack-4" onSubmit={(e) => { e.preventDefault(); void onVerifyOtp(); }}>
              <CodeInput length={6} value={otp} onChange={setOtp} label={t('auth.enterCode')} />
              <Button type="submit" block size="lg" busy={busy} disabled={otp.length !== 6}>
                {t('auth.verify')}
              </Button>
              <Button
                variant="ghost" block disabled={busy || resendIn > 0}
                onClick={() => void onSendOtp()}
              >
                {resendIn > 0 ? t('auth.resendIn', { s: resendIn }) : t('auth.resend')}
              </Button>
            </form>
          )}

          <div style={{ marginTop: 8 }}>
            <div className="t-xs t-muted" style={{ marginBottom: 6 }}>{t('app.language')}</div>
            <LanguageToggle />
          </div>

          <p className="t-xs t-faint" style={{ marginTop: 16 }}>
            {locale === 'hi'
              ? 'साइन इन करके आप हमारी गोपनीयता नीति से सहमत होते हैं।'
              : 'By signing in you agree to our privacy policy.'}
          </p>
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Digit-box code input
// ---------------------------------------------------------------------------

/**
 * Separate boxes per digit.
 *
 * A single text field for a 6-digit OTP is genuinely hard to use on a phone —
 * you cannot see where you are, and a mistyped digit means retyping the lot.
 * Boxes give position feedback and let paste-from-WhatsApp fill all six at
 * once.
 */
function CodeInput({
  length, value, onChange, secret, label,
}: {
  length: number;
  value: string;
  onChange: (v: string) => void;
  secret?: boolean;
  label: string;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus the first empty box when the value is cleared after a failed attempt.
  useEffect(() => {
    if (value === '') refs.current[0]?.focus();
  }, [value]);

  function setDigit(index: number, digit: string) {
    const chars = value.padEnd(length, ' ').split('');
    chars[index] = digit;
    const next = chars.join('').replace(/\s/g, '').slice(0, length);
    onChange(next);
    if (digit && index < length - 1) refs.current[index + 1]?.focus();
  }

  return (
    <div className="field">
      <label>{label}</label>
      <div className="codeinput">
        {Array.from({ length }).map((_, i) => (
          <input
            key={i}
            ref={(el) => { refs.current[i] = el; }}
            type={secret ? 'password' : 'text'}
            inputMode="numeric"
            autoComplete={secret ? 'off' : 'one-time-code'}
            maxLength={1}
            value={value[i] ?? ''}
            aria-label={`${label} ${i + 1}`}
            onChange={(e) => setDigit(i, e.target.value.replace(/\D/g, '').slice(-1))}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !value[i] && i > 0) refs.current[i - 1]?.focus();
              if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
              if (e.key === 'ArrowRight' && i < length - 1) refs.current[i + 1]?.focus();
            }}
            onPaste={(e) => {
              // Pasting the whole code from WhatsApp fills every box.
              e.preventDefault();
              const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
              if (pasted) { onChange(pasted); refs.current[Math.min(pasted.length, length - 1)]?.focus(); }
            }}
          />
        ))}
      </div>
    </div>
  );
}
